/**
 * Header Component
 * Top header med navigation och actions
 * Anpassad från deque-web-main
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from '../common/LanguageSwitcher';
import VersionDropdown from '../common/VersionDropdown';
import ImpersonationDropdown from './ImpersonationDropdown';
import { useTranslation } from '@/store/languageStore';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import PersonalTrackerAlarmBell from './PersonalTrackerAlarmBell';
import { PageBackButton } from '@/components/common/PageBackButton';
import { resolveViewTitle } from '@/utils/documentTitle';
import { useTheme } from 'next-themes';
import ModuleSearch from './ModuleSearch';

const Header = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const pageTitle = resolveViewTitle(pathname, t) ?? 'DQFleet';

  return (
    <div className="flex flex-col relative bg-bgContainer justify-between border-border border-b-[1px] w-full transition-all duration-400 ease-in-out">
      <div className="text-text-default py-2 px-4 text-2xl flex justify-between items-center font-semibold">
        <div className="flex items-center gap-2 min-w-0">
          {pathname !== ROUTES.HOME && pathname !== ROUTES.DASHBOARD && (
            <PageBackButton className="p-2 hover:bg-bgLight rounded-lg transition-colors shrink-0" />
          )}
          <div className="truncate">{pageTitle}</div>
        </div>
        <HeaderActions />
      </div>
    </div>
  );
};

/** Sök, larm, manual och växlarna till höger i headern. */
export function HeaderActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const iconColors = {
    search: isDark ? '#60a5fa' : '#6b7280',
    notification: isDark ? '#fbbf24' : '#6b7280',
    manual: isDark ? '#34d399' : '#6b7280',
  };

  return (
    <div className="flex items-center gap-2 relative">
      <ModuleSearch iconColor={iconColors.search} />
      <PersonalTrackerAlarmBell iconColor={iconColors.notification} />
      <button
        type="button"
        onClick={() => navigate(ROUTES.USER_MANUAL)}
        className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
        aria-label={t('manual.title')}
        title={t('manual.title')}
      >
        <span style={{ color: iconColors.manual, display: 'inline-flex' }}>
          <BookOpenIcon className="w-5 h-5" />
        </span>
      </button>
      <ImpersonationDropdown />
      <span data-guide="header-version"><VersionDropdown /></span>
      <span data-guide="header-language"><LanguageSwitcher /></span>
      <span data-guide="header-theme"><ThemeToggle /></span>
    </div>
  );
}

export default Header;
